import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useToastContext } from 'src/providers/ToastProvider/Toast.hook';
import { useWorkspaceContext } from 'src/providers/WorkspaceProvider/Workspace.context';
import { WalletService } from '../services/Wallet.service';
import { useWalletContext } from '../Wallet.context';
import {
  automaticChargeConfirmed,
  effectsFor,
  type tOutcomeEffects,
  type tResumptionCheck,
} from '../Wallet.outcome';
import { formatWalletAmount } from '../Wallet.money';
import type { tThresholdTopUpState, tTopUpOutcome } from '../Wallet.types';

/**
 * La forma en que la wallet confirma (RET-1928).
 *
 * UNA sola, y las tres superficies la pasan como `onOutcome`. Antes el camino
 * feliz cerraba la ventana y callaba: los caminos de error tenían alertas y el
 * éxito no, porque el único "aviso" previsto era implícito —que la pantalla
 * cambiara— y eso era justo lo que no ocurría.
 *
 * Sale por el `ToastProvider` de la aplicación, que ya está montado y tiene
 * ocho consumidores fuera de esta rebanada. No se construyó nada: la capacidad
 * ya existía y la wallet no la usaba ni una vez.
 *
 * EL AVISO SE VA A LOS 5 s Y ESO ES DELIBERADO, con su consecuencia asumida.
 * Lo que persiste no es el aviso sino el ESTADO: la tarjeta queda con el switch
 * encendido, su monto y su cadencia, porque `applyOutcome` invalidó la
 * configuración y el hook de la tarjeta releyó el back. El aviso explica; la
 * tarjeta sostiene.
 *
 * No decide nada por su cuenta. Qué se relee y si hay aviso lo dice la tabla de
 * `Wallet.outcome.ts`; aquí sólo se compone la frase y se llama a quien
 * corresponde.
 *
 * La única excepción es la relectura de RET-2426 (U6): cuando la tabla dice que
 * la recarga única terminó la pausa (`confirmsResumption`), este hook lee el GET
 * de la recarga por saldo bajo y emite el desenlace `threshold-resumed` con lo
 * que vio. Vive aquí y no en el modal porque corre después de que el modal se
 * cerró y se desmontó, y este hook sigue montado con la pantalla que lo abrió.
 */
export function useWalletOutcome(): (outcome: tTopUpOutcome) => void {
  const { applyOutcome } = useWalletContext();
  const { addAlert } = useToastContext();
  const { t, i18n } = useTranslation('wallet');
  const { currentWorkspace } = useWorkspaceContext();

  /**
   * Una sola traducción desenlace -> invalidar + avisar. La usan el desenlace
   * que llega del modal y el que arma la relectura de abajo.
   */
  const paint = useCallback(
    (outcome: tTopUpOutcome): tOutcomeEffects => {
      applyOutcome(outcome);

      const effects = effectsFor(outcome);
      const { notice } = effects;

      if (notice) {
        addAlert({
          type: notice.type,
          title: t(notice.titleKey),
          message: t(notice.messageKey, notice.vars),
        });
      }

      return effects;
    },
    [applyOutcome, addAlert, t],
  );

  /*
   * La relectura (U6) corre DESPUÉS de que el modal se cerró y se desmontó, así
   * que no puede vivir en él: este hook sigue montado con la pantalla que abrió
   * el modal, y es quien ve el workspace actual. Se lee todo por ref porque la
   * continuación espera una red y no debe decidir con los valores del render que
   * la lanzó (el workspace, el `paint` de entonces).
   */
  const paintRef = useRef(paint);
  paintRef.current = paint;
  const workspaceRef = useRef(currentWorkspace?.id ?? '');
  workspaceRef.current = currentWorkspace?.id ?? '';
  const i18nRef = useRef(i18n);
  i18nRef.current = i18n;
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
    };
  }, []);

  /**
   * KTD9: la única terminó la pausa. Lo que el cable dijo es que se reanudó, y
   * eso es lo único que se afirma salvo que ESTA relectura, posterior al pago,
   * muestre un cobro nuevo de la automática. Si la lectura falla, se dice lo
   * que se sabe. Si el workspace de la pantalla ya no es el del pago (o la
   * pantalla se fue), nada de esto aplica: la lectura vieja no se pinta.
   */
  const confirmResumption = useCallback(
    async (check: tResumptionCheck): Promise<void> => {
      const workspaceId = check.workspaceId ?? workspaceRef.current;
      const isStale = () =>
        !mountedRef.current ||
        workspaceId === '' ||
        workspaceRef.current !== workspaceId;

      if (isStale()) return;

      let state: tThresholdTopUpState | null;

      try {
        state = await WalletService.getThresholdTopUp(workspaceId);
      } catch {
        state = null;
      }

      if (isStale()) return;

      const charged =
        state !== null &&
        automaticChargeConfirmed({
          state,
          chargesBefore: check.chargesBefore,
        });
      // El monto del cobro que se vio: el configurado (KTD11), que es el que
      // la automática cobra. Nunca el de un `chargeInProgress`, que cae a cero
      // si no se conoce. Sin cifra, el aviso no afirma.
      const amount = state === null ? null : state.amount;

      paintRef.current({
        kind: 'threshold-resumed',
        charged,
        amountLabel:
          charged && state !== null && amount !== null
            ? formatWalletAmount({
                amountMicros: amount.amountMicros,
                currency: state.currency,
                locale: i18nRef.current.language,
                trimZeroCents: true,
              })
            : '',
      });
    },
    [],
  );

  return useCallback(
    (outcome: tTopUpOutcome): void => {
      const { confirmsResumption } = paint(outcome);

      if (confirmsResumption) void confirmResumption(confirmsResumption);
    },
    [paint, confirmResumption],
  );
}
